import jsCookies from 'js-cookie';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';
import { type ComponentType, useEffect, useMemo, useState } from 'react';

import PageLoading from 'src/components/common/page-loading/page-loading';
import { routes } from 'src/constants/navigation';
import { useStores } from '../../common/root-store-provider/root-store-provider';
import { useAbortableEffect } from 'src/utils/hooks';

const page = <T extends object>(Component: ComponentType<T>) =>
  observer((props: T) => {
    const [mounted, setMounted] = useState(false);
    const {
      authStore: { variant, fetchUser, auth_token, user },
      analyticsStore: { getAbTests, segment, isFetching },
    } = useStores();
    const router = useRouter();
    const { route, query, isReady } = router;
    const googlePseudoIdCurrent = jsCookies.get('_ga')?.slice(6);

    const emailToken =
      typeof query['email_token'] === 'string'
        ? query['email_token']
        : undefined;

    const result = useMemo(() => {
      if (!route.includes(routes.quiz) && (!mounted || user === undefined)) {
        return <PageLoading />;
      } else {
        return <Component {...props} />;
      }
    }, [route, mounted, user, Component, props]);

    useEffect(() => {
      setMounted(true);
    }, []);

    useEffect(() => {
      router.prefetch(routes.quiz).then((r) => r);
    }, [router]);

    useAbortableEffect(() => {
      if (emailToken === undefined || variant !== 'emailtraf') {
        // noinspection JSIgnoredPromiseFromCall
        fetchUser({ token: auth_token });
      }
    }, [emailToken, fetchUser, auth_token, variant]);

    useAbortableEffect(() => {
      if (
        !segment &&
        !isFetching &&
        query['reset'] === undefined &&
        query['hard_reset'] === undefined &&
        isReady
      ) {
        getAbTests({
          variant: variant ?? '',
          utm_source: (query['utm_source'] as string) ?? '',
          utm_campaign: (query['utm_campaign'] as string) ?? '',
          region: (query['region'] as string) ?? '',
          user_id: googlePseudoIdCurrent,
        }).then((r) => r);
      }
    }, [getAbTests, isReady, query, segment, variant, googlePseudoIdCurrent]);

    // The check on `mounted` helps avoid hydration DOM mismatch
    // that happens because `user` is always empty during SSR,
    // but may be `null` if the user is not logged in
    return result;
  });

export default page;
